import { useMemo } from 'react';
import * as THREE from 'three';
import { Line, Text } from '@react-three/drei';
import { ThreeEvent } from '@react-three/fiber';
import { useDimStyleStore, dimStyleColor } from './DimensioningTools';
import { useAnnotationInk } from '../../hooks/useThemeInk';

/**
 * Subtype-true dimension renderers (docs/plans/2d plan A1): angular,
 * arc-length, ordinate, jogged radius and geometric-tolerance feature
 * control frames draw with their OWN geometry instead of falling back to
 * the linear/chord renderer.
 */

interface RendererProps {
    data: Record<string, any>;
    isSelected: boolean;
    onClick: (e: ThreeEvent<MouseEvent>) => void;
}

const V = (p: number[] | undefined): THREE.Vector3 =>
    new THREE.Vector3(p?.[0] ?? 0, p?.[1] ?? 0, p?.[2] ?? 0);

function useDimStyle(data: Record<string, any>) {
    return useDimStyleStore((s) => s.getStyleFor(data.styleId as string | undefined));
}

function ArrowHead({ position, angle, size, color }: {
    position: THREE.Vector3; angle: number; size: number; color: string;
}) {
    const pts = useMemo(() => {
        const back1 = new THREE.Vector3(
            position.x - Math.cos(angle - 0.3) * size,
            position.y - Math.sin(angle - 0.3) * size,
            position.z
        );
        const back2 = new THREE.Vector3(
            position.x - Math.cos(angle + 0.3) * size,
            position.y - Math.sin(angle + 0.3) * size,
            position.z
        );
        return [back1, position, back2];
    }, [position, angle, size]);
    return <Line points={pts} color={color} lineWidth={1.5} />;
}

/**
 * ANGULAR dimension: two rays from the vertex + the measurement arc with
 * the degree value at the arc midpoint.
 */
export const AngularDimensionRenderer = ({ data, isSelected, onClick }: RendererProps) => {
    const style = useDimStyle(data);
    const color = isSelected ? '#00aaff' : dimStyleColor(style.lineColor, useAnnotationInk());
    const { rays, arc, textPos, label, midAngle } = useMemo(() => {
        const c = V(data.center);
        const p1 = V(data.point1), p2 = V(data.point2);
        let a0 = Math.atan2(p1.y - c.y, p1.x - c.x);
        let a1 = Math.atan2(p2.y - c.y, p2.x - c.x);
        let sweep = a1 - a0;
        while (sweep <= 0) sweep += Math.PI * 2;
        const r1 = Math.max(c.distanceTo(p1), 1e-4), r2 = Math.max(c.distanceTo(p2), 1e-4);
        const arcR = Math.min(r1, r2) * 0.45;
        const pts: THREE.Vector3[] = [];
        const n = Math.max(8, Math.round(sweep / (Math.PI / 24)));
        for (let i = 0; i <= n; i++) {
            const a = a0 + (sweep * i) / n;
            pts.push(new THREE.Vector3(c.x + Math.cos(a) * arcR, c.y + Math.sin(a) * arcR, c.z));
        }
        const midA = a0 + sweep / 2;
        const tp = new THREE.Vector3(c.x + Math.cos(midA) * (arcR + style.textHeight),
            c.y + Math.sin(midA) * (arcR + style.textHeight), c.z + style.textGap);
        const deg = typeof data.angle === 'number' ? data.angle
            : Number(String(data.override ?? '').replace('°', '')) || (sweep * 180 / Math.PI);
        void a1;
        return {
            rays: [[c, p1], [c, p2]] as [THREE.Vector3, THREE.Vector3][],
            arc: pts,
            textPos: tp,
            label: String(data.override ?? `${deg.toFixed(Math.max(0, style.precision - 1))}°`),
            midAngle: midA,
        };
    }, [data, style]);
    void midAngle;
    return (
        <group onClick={(e) => { e.stopPropagation(); onClick(e); }}>
            {rays.map(([a, b], i) => (
                <Line key={i} points={[a, b]} color={color} lineWidth={style.lineWeight} />
            ))}
            <Line points={arc} color={color} lineWidth={style.lineWeight} />
            <Text position={textPos} color={dimStyleColor(style.textColor, useAnnotationInk())} fontSize={style.textHeight} anchorX="center" anchorY="bottom">
                {label}
            </Text>
        </group>
    );
};

/**
 * ARC LENGTH dimension: the measurement follows the tessellated arc path
 * (not the end-to-end chord) with arrow ticks at both ends.
 */
export const ArcLengthDimensionRenderer = ({ data, isSelected, onClick }: RendererProps) => {
    const style = useDimStyle(data);
    const color = isSelected ? '#00aaff' : dimStyleColor(style.lineColor, useAnnotationInk());
    const path = useMemo(() => {
        const pts = (data.arcPts as number[][]) ?? [];
        if (pts.length >= 2) return pts.map(V);
        // Legacy payloads without tessellation: chord fallback (honest).
        return [V(data.point1), V(data.point2)];
    }, [data]);
    const textPos = V(data.textPosition).add(new THREE.Vector3(0, 0, style.textGap));
    const label = String(data.override ?? (typeof data.arcLength === 'number'
        ? `${data.arcLength.toFixed(style.precision)}${style.suffix}` : ''));
    const dir = path.length >= 2
        ? path[path.length - 1].clone().sub(path[path.length - 2]).normalize()
        : new THREE.Vector3(1, 0, 0);
    const ang = Math.atan2(dir.y, dir.x);
    return (
        <group onClick={(e) => { e.stopPropagation(); onClick(e); }}>
            <Line points={path} color={color} lineWidth={style.lineWeight} />
            <ArrowHead position={path[0]} angle={ang + Math.PI} size={style.arrowSize} color={color} />
            <ArrowHead position={path[path.length - 1]} angle={ang} size={style.arrowSize} color={color} />
            <Text position={textPos} color={dimStyleColor(style.textColor, useAnnotationInk())} fontSize={style.textHeight} anchorX="center" anchorY="bottom">
                {label}
            </Text>
        </group>
    );
};

/**
 * ORDINATE dimension: leader from the feature point to the label stub,
 * with the axis (X/Y) value; a short reference stub marks the datum side.
 */
export const OrdinateDimensionRenderer = ({ data, isSelected, onClick }: RendererProps) => {
    const style = useDimStyle(data);
    const color = isSelected ? '#00aaff' : dimStyleColor(style.lineColor, useAnnotationInk());
    const feature = V(data.point1), end = V(data.point2 ?? data.textPosition);
    const axis = data.axis === 'x' ? 'X' : 'Y';
    const label = String(data.override ?? '');
    const stub = useMemo(() => {
        const dirX = axis === 'X' ? new THREE.Vector3(0, 0.35, 0) : new THREE.Vector3(0.35, 0, 0);
        return [end.clone(), end.clone().add(dirX)] as [THREE.Vector3, THREE.Vector3];
    }, [end, axis]);
    const textPos = useMemo(() => {
        const away = axis === 'X' ? new THREE.Vector3(0, 0.1, 0) : new THREE.Vector3(0.1, 0, 0);
        return end.clone().add(away).add(new THREE.Vector3(0, 0, style.textGap));
    }, [end, axis, style.textGap]);
    return (
        <group onClick={(e) => { e.stopPropagation(); onClick(e); }}>
            <Line points={[feature, end]} color={color} lineWidth={style.lineWeight} />
            <Line points={[stub[0], stub[1]]} color={color} lineWidth={style.lineWeight} />
            <Text position={textPos} color={dimStyleColor(style.textColor, useAnnotationInk())} fontSize={style.textHeight} anchorX="left" anchorY="bottom">
                {`${axis} ${label}`}
            </Text>
        </group>
    );
};

/**
 * JOGGED radius: center→jog→rim leader with a zigzag at the jog (the center
 * is off-page), plus the R value. Never touches the linear spread path.
 */
export const JoggedDimensionRenderer = ({ data, isSelected, onClick }: RendererProps) => {
    const style = useDimStyle(data);
    const color = isSelected ? '#00aaff' : dimStyleColor(style.lineColor, useAnnotationInk());
    const center = V(data.center), rim = V(data.radiusPoint), jog = V(data.textPosition ?? undefined);
    const { zig, line } = useMemo(() => {
        const d = jog.clone().sub(center).normalize();
        const p = new THREE.Vector3(-d.y, d.x, 0).multiplyScalar(0.08);
        const t = 0.55; // jog sits ~55% along the center->rim span
        const base = center.clone().lerp(rim, t);
        const z1 = base.clone().addScaledVector(p, 1);
        const z2 = base.clone().addScaledVector(p, -1);
        return {
            zig: [base, z1, z2, base.clone().addScaledVector(d, (rim.distanceTo(center)) * 0.06)],
            line: [center, base, z1, z2, rim],
        };
    }, [center, rim, jog]);
    void zig;
    const mid = center.clone().lerp(rim, 0.62).add(new THREE.Vector3(0, 0, style.textGap + 0.05));
    const value = typeof data.radius === 'number' ? data.radius : center.distanceTo(rim);
    const label = String(data.override ?? `R${(value * style.scale).toFixed(style.precision)}${style.suffix}`);
    const ang = Math.atan2(rim.y - center.y, rim.x - center.x);
    return (
        <group onClick={(e) => { e.stopPropagation(); onClick(e); }}>
            <Line points={line} color={color} lineWidth={style.lineWeight} />
            <ArrowHead position={rim} angle={ang} size={style.arrowSize} color={color} />
            <Text position={mid} color={dimStyleColor(style.textColor, useAnnotationInk())} fontSize={style.textHeight} anchorX="center" anchorY="bottom">
                {label}
            </Text>
        </group>
    );
};

/**
 * TOLERANCE feature control frame (A2): a bordered box split into cells —
 * [symbol][tolerance][datum…] — the GD&T FCF 2D's TOLERANCE creates.
 */
export const ToleranceFrameRenderer = ({ data, isSelected, onClick }: RendererProps) => {
    const cells = (data.cells as string[]) ?? [];
    const pos = V(data.position);
    const h = (data.height as number) || 0.3;
    const color = isSelected ? '#00aaff' : (data.color as string) || useAnnotationInk();
    const widths = cells.map((c) => Math.max(h * 1.2, 0.28 + String(c).length * h * 0.55));
    const total = widths.reduce((a, b) => a + b, 0);
    let x = 0;
    const boxes = cells.map((c, i) => {
        const w = widths[i];
        const bx = x;
        x += w;
        return { c, bx, w };
    });
    return (
        <group position={[pos.x, pos.y, pos.z + 0.03]} onClick={(e) => { e.stopPropagation(); onClick(e); }}>
            <Line
                points={[[0, 0, 0], [total, 0, 0], [total, h, 0], [0, h, 0], [0, 0, 0]]}
                color={color}
                lineWidth={1.5}
            />
            {boxes.map((b, i) => (i > 0 && (
                <Line key={i} points={[[b.bx, 0, 0], [b.bx, h, 0]]} color={color} lineWidth={1.5} />
            )))}
            {boxes.map((b, i) => (
                <Text
                    key={`t${i}`}
                    position={[b.bx + b.w / 2, h / 2, 0.005]}
                    fontSize={h * 0.62}
                    color={color}
                    anchorX="center"
                    anchorY="middle"
                >
                    {b.c}
                </Text>
            ))}
        </group>
    );
};
